// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { formatPersonName } from '@/account/utilities/format-person-name';
import { CostAdjustment } from '@/asset/api/types';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import {
  COST_ADJUSTMENT_REASON_LABELS,
  COST_ADJUSTMENT_TYPE_LABELS,
} from '@/constants/cost-adjustment';
import { SortDirection, invert } from '@/constants/sort';
import { formatCurrencyValue } from '@/currency/utilities/format';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

interface Props {
  adjustments: CostAdjustment[];
}

type SortField =
  | 'effectiveDate'
  | 'adjustmentType'
  | 'reason'
  | 'amount'
  | 'notes'
  | 'createdBy'
  | 'createdAt';

interface Column {
  align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
  field: SortField;
  label: string;
}

const columns: Column[] = [
  { field: 'effectiveDate', label: 'Date' },
  { field: 'adjustmentType', label: 'Type' },
  { field: 'amount', label: 'Adjustment amount', align: 'right' },
  { field: 'reason', label: 'Reason' },
  { field: 'notes', label: 'Notes' },
  { field: 'createdBy', label: 'Recorded by' },
  { field: 'createdAt', label: 'Recorded at' },
];

function getSortValue(adjustment: CostAdjustment, field: SortField) {
  switch (field) {
    case 'effectiveDate':
      return adjustment.effectiveDate.toMillis();
    case 'adjustmentType':
      return COST_ADJUSTMENT_TYPE_LABELS[adjustment.adjustmentType];
    case 'reason':
      return COST_ADJUSTMENT_REASON_LABELS[adjustment.reason];
    case 'amount':
      return adjustment.adjustmentType === 'CAPITALIZATION'
        ? adjustment.amount.amount.toNumber()
        : -adjustment.amount.amount.toNumber();
    case 'notes':
      return adjustment.notes ?? '';
    case 'createdBy':
      return formatPersonName(adjustment.createdBy);
    case 'createdAt':
      return adjustment.createdAt.toMillis();
  }
}

export function CostAdjustmentTable(props: Props) {
  const [sortField, setSortField] = useState<SortField>('effectiveDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedAdjustments = useMemo(() => {
    const adjustments = [...props.adjustments];

    adjustments.sort((left, right) => {
      const leftValue = getSortValue(left, sortField);
      const rightValue = getSortValue(right, sortField);

      let comparison = 0;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return adjustments;
  }, [props.adjustments, sortDirection, sortField]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(invert);
      return;
    }

    setSortField(field);
    setSortDirection(
      field === 'effectiveDate' || field === 'createdAt' ? 'desc' : 'asc'
    );
  };

  if (props.adjustments.length === 0) {
    return (
      <Paper sx={{ px: 2, py: 3 }} variant="outlined">
        <Typography color="text.secondary" variant="body2">
          No cost adjustments have been recorded for this asset yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align}
                sortDirection={
                  sortField === column.field ? sortDirection : false
                }
              >
                <TableSortLabel
                  active={sortField === column.field}
                  direction={sortField === column.field ? sortDirection : 'asc'}
                  onClick={() => handleSort(column.field)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAdjustments.map((adjustment) => {
            const amountPrefix =
              adjustment.adjustmentType === 'CAPITALIZATION' ? '+' : '-';

            return (
              <TableRow key={adjustment.key}>
                <TableCell>
                  {adjustment.effectiveDate.toFormat('yyyy-MM-dd')}
                </TableCell>
                <TableCell>
                  {COST_ADJUSTMENT_TYPE_LABELS[adjustment.adjustmentType]}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color:
                      adjustment.adjustmentType === 'CAPITALIZATION'
                        ? 'success.main'
                        : 'error.main',
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {amountPrefix}
                  {formatCurrencyValue(adjustment.amount, { sparse: true })}
                </TableCell>
                <TableCell>
                  {COST_ADJUSTMENT_REASON_LABELS[adjustment.reason]}
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  {adjustment.notes ?? '—'}
                </TableCell>
                <TableCell>{formatPersonName(adjustment.createdBy)}</TableCell>
                <TableCell>
                  <MultiTimezoneTooltip timestamp={adjustment.createdAt}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                      }}
                    >
                      {adjustment.createdAt.toLocaleString({
                        dateStyle: 'medium',
                      })}
                    </Typography>
                  </MultiTimezoneTooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
